/**
 * Hooks TanStack Query per FASE 4 — voti, formazioni, giornate, risultati.
 *
 * Bridge v1.4 Sez. 4.9.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export type StatoGiornata = "da_giocare" | "voti_caricati" | "calcolata" | "annullata";

export interface GiornataItem {
  id: string;
  stagione_id: string;
  numero: number;
  numero_serie_a: number;
  stato: StatoGiornata;
  versione_calcolo_corrente: number;
  data_calcolo: string | null;
  banner_ricalcolo_in_corso: boolean;
}

/** Una sostituzione fatta dal calcolo (titolare SV -> panchinaro). */
export interface SostituzioneItem {
  titolare_id: string;
  sostituto_id: string;
}

export interface RisultatoItem {
  id: string;
  squadra_id: string;
  giornata_id: string;
  punteggio_voto: string;
  bonus_capitano: string;
  bonus_difesa: string;
  bonus_fairplay: string;
  punteggio_totale: string;
  gol_fantacalcio: number;
  versione: number;
  data_calcolo: string;
  /** Andrea 2026-09-13: OUT/IN reali del calcolo. */
  dettaglio_sostituzioni?: SostituzioneItem[];
  /** Andrea 2026-10-30: un risultato per competizione (null = vale per tutte). */
  competizione_id?: string | null;
}

export interface AnnullamentoItem {
  id: string;
  giornata_id: string;
  versione_annullata: number;
  motivazione: string;
  data_annullamento: string;
  nuova_versione_calcolata: number | null;
}

export interface FormazioneItem {
  id: string;
  squadra_id: string;
  giornata_id: string;
  competizione_id: string;
  modulo: string;
  combinazione_id: string | null;
  titolari_ids: string[];
  panchina_ids: string[];
  // Andrea 2026-05-21: cap/vice ora opzionali (null = non designato)
  capitano_id: string | null;
  vicecapitano_id: string | null;
  consegnata_at: string | null;
  /** Interventi dell'admin, dal piu' recente (Christian 2026-11-03). */
  modifiche_admin?: ModificaAdminItem[];
}

export interface ModificaAdminItem {
  admin_nome: string;
  inserita: boolean;
  cambi: string[];
  created_at: string;
}

export interface FormazionePayload {
  squadra_id: string;
  giornata_id: string;
  competizione_id: string;
  modulo: string;
  combinazione_id?: string | null;
  titolari_ids: string[];
  panchina_ids?: string[];
  capitano_id?: string | null;
  vicecapitano_id?: string | null;
  consegna?: boolean;
  /** Inserimento da admin/presidente di lega: salta deadline e stato giornata. */
  forzata?: boolean;
}

export interface CombinazioneItem {
  id: string;
  modulo: string;
  modalita: "mantra" | "classic";
  combinazione: string[];
  fonte: string;
}

export interface ImportVotiPayload {
  tipo: "live" | "finale";
  fonte: "scraper" | "csv_manuale" | "paste_manuale" | "manuale_admin";
  csv?: string | null;
  paste?: string | null;
  html_gazzetta?: string | null;
  /** XLSX Fantacalcio.it codificato base64 (alternativa a multipart upload). */
  xlsx_b64?: string | null;
  soglia_fuzzy?: number;
}

export interface ImportVotiReport {
  voti_inseriti: number;
  nomi_non_abbinati: Array<[string, string | null]>;
  nomi_fuzzy_sotto_soglia: Array<[string, string | null, Array<[string, number]>]>;
}

// === Queries ===

export function useGiornata(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["giornata", giornataId],
    queryFn: async () => {
      const res = await api.get<GiornataItem>(`/giornate/${giornataId}`);
      return res.data;
    },
    enabled: isAuth && !!giornataId,
  });
}

export function useRisultatiGiornata(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["risultati", giornataId],
    queryFn: async () => {
      const res = await api.get<RisultatoItem[]>(`/giornate/${giornataId}/risultati`);
      return res.data;
    },
    enabled: isAuth && !!giornataId,
  });
}

export function useAnnullamenti(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["annullamenti", giornataId],
    queryFn: async () => {
      const res = await api.get<AnnullamentoItem[]>(`/giornate/${giornataId}/annullamenti`);
      return res.data;
    },
    enabled: isAuth && !!giornataId,
  });
}

export interface VotoGiocatoreItem {
  giocatore_id: string;
  voto_base: string | null; // null = SV
  gol_fatti: number;
  gol_subiti: number;
  autogol: number;
  ammonizioni: number;
  espulsioni: number;
  assist: number;
  porta_inviolata: boolean;
  rigore_segnato: number;
  rigore_sbagliato: number;
  rigore_parato: number;
  voto_finale: string | null;
  tipo: "live" | "finale";
  /** Club Serie A del giocatore (Andrea 2026-09-12): se un club ha voti
   * caricati, la sua partita e' stata giocata. */
  club_serie_a?: string | null;
}

/** Voti giocatore (live + bonus/malus) per una giornata.
 *
 * NB: hits backend only (legge da DB locale `voti_gazzetta`), NON football-data.
 * Il refresh interval e' 15 min perche':
 *   1. i voti live si aggiornano dal cron scraper Gazzetta ~ogni 15 min
 *   2. polling piu' aggressivo e' spreco di risorse backend
 *   3. coerenza con la cadenza tipica del fantacalcio
 */
export function useVotiGiocatoriGiornata(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["voti-giocatori", giornataId],
    queryFn: async () => {
      const res = await api.get<VotoGiocatoreItem[]>(`/giornate/${giornataId}/voti-giocatori`);
      return res.data;
    },
    enabled: isAuth && !!giornataId,
    refetchInterval: 15 * 60 * 1000, // 15 min
    staleTime: 15 * 60 * 1000,
  });
}

export interface ProbabilitaGiocatoreItem {
  giocatore_id: string;
  probabilita: number;
  fonte: string;
  note: string | null;
}

/** Probabili formazioni per la giornata. Andrea 2026-05-19: import
 * manuale paste, vedi /admin/probabili. La Schiera Formazione usa
 * queste % per mostrare badge colore accanto al nome. */
export function useProbabiliGiornata(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["probabili", giornataId],
    queryFn: async () => {
      const res = await api.get<ProbabilitaGiocatoreItem[]>(`/giornate/${giornataId}/probabili`);
      return res.data;
    },
    enabled: isAuth && !!giornataId,
    staleTime: 15 * 60 * 1000,
  });
}

export interface AvversarioSAItem {
  club: string;
  avversario: string;
  in_casa: boolean;
  data_ora_inizio: string;
  stato: string;
}

/** Calendario Serie A della giornata girato per club (Andrea 2026-09-09):
 * in Schiera Formazione, accanto al club di ogni giocatore, si vede
 * contro chi gioca davvero. */
export function useAvversariSerieA(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["avversari-serie-a", giornataId],
    queryFn: async () => {
      const res = await api.get<AvversarioSAItem[]>(`/giornate/${giornataId}/avversari-serie-a`);
      return res.data;
    },
    enabled: isAuth && !!giornataId,
    staleTime: 60 * 60 * 1000, // il calendario SA cambia raramente
  });
}

export function useFormazione(
  squadraId: string | null,
  giornataId: string | null,
  competizioneId: string | null = null,
  /** Andrea 2026-10-30: la formazione che il calcolo usa per la competizione
   *  (la sua se consegnata, altrimenti l'ultima consegnata). Pagina partita. */
  comeCalcolo = false,
) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["formazione", squadraId, giornataId, competizioneId, comeCalcolo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (competizioneId) params.set("competizione_id", competizioneId);
      if (comeCalcolo) params.set("come_calcolo", "true");
      const qs = params.size > 0 ? `?${params.toString()}` : "";
      const res = await api.get<FormazioneItem | null>(
        `/formazioni/${squadraId}/${giornataId}${qs}`,
      );
      return res.data;
    },
    enabled: isAuth && !!squadraId && !!giornataId,
  });
}

export function useCombinazioni(modalita: "mantra" | "classic" | null = null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["combinazioni", modalita],
    queryFn: async () => {
      const params = modalita ? `?modalita=${modalita}` : "";
      const res = await api.get<CombinazioneItem[]>(`/moduli-combinazioni${params}`);
      return res.data;
    },
    enabled: isAuth,
  });
}

// === Mutations ===

export function useSalvaFormazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FormazionePayload) => {
      const res = await api.post<FormazioneItem>("/formazioni", payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["formazione"] });
    },
  });
}

/** Rollback giornata da voti_caricati → da_giocare. Admin only. */
export function useRiapriGiornata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (giornataId: string) => {
      const res = await api.post<{ giornata_id: string; stato: string }>(
        `/admin/giornate/${giornataId}/riapri`,
      );
      return res.data;
    },
    onSuccess: (_, gid) => {
      qc.invalidateQueries({ queryKey: ["giornata", gid] });
      qc.invalidateQueries({ queryKey: ["stagione-giornate"] });
    },
  });
}

export function useCalcolaGiornata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (giornataId: string) => {
      const res = await api.post(`/admin/giornate/${giornataId}/calcola`);
      return res.data;
    },
    onSuccess: (_, gid) => {
      qc.invalidateQueries({ queryKey: ["giornata", gid] });
      qc.invalidateQueries({ queryKey: ["risultati", gid] });
      qc.invalidateQueries({ queryKey: ["recap-partita"] });
    },
  });
}

export function useAnnullaGiornata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      giornataId,
      motivazione,
    }: {
      giornataId: string;
      motivazione: string;
    }) => {
      const res = await api.post(`/admin/giornate/${giornataId}/annulla`, { motivazione });
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["giornata", vars.giornataId] });
      qc.invalidateQueries({ queryKey: ["annullamenti", vars.giornataId] });
      qc.invalidateQueries({ queryKey: ["risultati", vars.giornataId] });
      qc.invalidateQueries({ queryKey: ["recap-partita"] });
    },
  });
}

export function useRicalcolaGiornata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (giornataId: string) => {
      const res = await api.post(`/admin/giornate/${giornataId}/ricalcola`);
      return res.data;
    },
    onSuccess: (_, gid) => {
      qc.invalidateQueries({ queryKey: ["giornata", gid] });
      qc.invalidateQueries({ queryKey: ["risultati", gid] });
      qc.invalidateQueries({ queryKey: ["recap-partita"] });
    },
  });
}

export type DecisioneRinvio = "rinvia_giornata_fanta" | "sei_politico";

export interface RinvioItem {
  id: string;
  giornata_id: string;
  squadra_serie_a: string;
  decisione: DecisioneRinvio;
  motivazione: string | null;
  decisa_da_user_id: string;
  created_at: string;
}

export interface RegistraRinvioPayload {
  squadra_serie_a: string;
  decisione: DecisioneRinvio;
  motivazione?: string | null;
  applica_sei_politico?: boolean;
  voto_override?: string | null;
}

export interface ConflittoItem {
  giocatore_id: string;
  giocatore_nome: string;
  voto_6_politico_id: string;
  voto_6_politico_at: string;
  voto_gazzetta_id: string;
  voto_gazzetta_at: string;
}

export function useRinvii(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["rinvii", giornataId],
    queryFn: async () => {
      const res = await api.get<RinvioItem[]>(`/admin/giornate/${giornataId}/rinvii`);
      return res.data;
    },
    enabled: isAuth && !!giornataId,
  });
}

export function useConflittiSeiPolitico(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["conflitti-sei-politico", giornataId],
    queryFn: async () => {
      const res = await api.get<ConflittoItem[]>(`/giornate/${giornataId}/conflitti-sei-politico`);
      return res.data;
    },
    enabled: isAuth && !!giornataId,
  });
}

export function useRegistraRinvio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      giornataId,
      payload,
    }: {
      giornataId: string;
      payload: RegistraRinvioPayload;
    }) => {
      const res = await api.post<RinvioItem>(`/admin/giornate/${giornataId}/rinvii`, payload);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["rinvii", vars.giornataId] });
    },
  });
}

export function useImportVoti() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      giornataId,
      payload,
    }: {
      giornataId: string;
      payload: ImportVotiPayload;
    }) => {
      // Timeout esteso a 120s: import XLSX di 220+ voti con fuzzy match
      // nomi puo' superare i 30s di default (parsing + DB insert).
      const res = await api.post<ImportVotiReport>(
        `/admin/giornate/${giornataId}/import-voti`,
        payload,
        { timeout: 120000 },
      );
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["giornata", vars.giornataId] });
      // Invalida anche i voti-giocatori per la giornata: cosi' se l'utente
      // naviga su /partita subito dopo l'import, vede i nuovi voti.
      qc.invalidateQueries({ queryKey: ["voti-giocatori", vars.giornataId] });
    },
  });
}

// ============================================================
// Admin · App — Voti caricati (Andrea 2026-10-16)
// ============================================================
export interface VotoAdminItem {
  id: string;
  giocatore_id: string;
  nome: string;
  club_serie_a: string | null;
  ruolo_classic: string | null;
  ruoli_mantra: string[] | null;
  voto_base: string | null;
  gol_fatti: number;
  gol_subiti: number;
  autogol: number;
  ammonizioni: number;
  espulsioni: number;
  assist: number;
  porta_inviolata: boolean;
  rigore_segnato: number;
  rigore_sbagliato: number;
  rigore_parato: number;
  voto_finale: string | null;
  tipo: "live" | "finale";
  fonte: string;
  versione: number;
  data_import: string;
}

export interface VotiAdminResponse {
  giornata_stato: string;
  voti: VotoAdminItem[];
}

export interface ModificaVotoPayload {
  voto_base?: string | null;
  gol_fatti?: number;
  gol_subiti?: number;
  autogol?: number;
  ammonizioni?: number;
  espulsioni?: number;
  assist?: number;
  porta_inviolata?: boolean;
  rigore_segnato?: number;
  rigore_sbagliato?: number;
  rigore_parato?: number;
}

/** Tutti i voti attivi della giornata, col giocatore (solo admin app). */
export function useVotiGiornataAdmin(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["voti-admin", giornataId],
    queryFn: async () =>
      (await api.get<VotiAdminResponse>(`/admin/giornate/${giornataId}/voti`)).data,
    enabled: isAuth && !!giornataId,
  });
}

/** Corregge un voto: il backend crea una nuova versione (append-only) e la
 * propaga alle giornate sorelle. */
export function useModificaVoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      votoId,
      giornataId: _g,
      payload,
    }: {
      votoId: string;
      giornataId: string;
      payload: ModificaVotoPayload;
    }) => (await api.patch<VotoAdminItem>(`/admin/voti/${votoId}`, payload)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["voti-admin", vars.giornataId] });
      qc.invalidateQueries({ queryKey: ["voti-giocatori", vars.giornataId] });
    },
  });
}
